import api from './api';
import { Entity } from '../types';
import { demoDataService } from './demoDataService';
import { config } from '../config';

export const entityApi = {
  // Get all entities
  getEntities: async (): Promise<Entity[]> => {
    if (config.shouldUseDemoMode()) {
      return demoDataService.getEntities();
    }
    
    try {
      const response = await api.get('/entities');
      return response.data;
    } catch (error: any) {
      console.warn('Backend not available, falling back to demo mode');
      return demoDataService.getEntities();
    }
  },

  // Get entity by ID
  getEntity: async (entityId: string): Promise<Entity> => {
    if (config.shouldUseDemoMode()) {
      const entities = await demoDataService.getEntities();
      const entity = entities.find(e => e.id === entityId);
      if (!entity) throw new Error('Entity not found');
      return entity;
    }
    
    try {
      const response = await api.get(`/entities/${entityId}`);
      return response.data;
    } catch (error: any) {
      console.warn('Backend not available, falling back to demo mode');
      const entities = await demoDataService.getEntities();
      const entity = entities.find(e => e.id === entityId);
      if (!entity) throw new Error('Entity not found');
      return entity;
    }
  },

  // Create entity
  createEntity: async (entityData: any): Promise<Entity> => {
    if (config.shouldUseDemoMode()) {
      return demoDataService.createEntity(entityData);
    }
    
    const response = await api.post('/entities', entityData);
    return response.data;
  },

  // Update entity
  updateEntity: async (entityId: string, entityData: any): Promise<Entity> => {
    if (config.shouldUseDemoMode()) {
      throw new Error('Entity update not available in demo mode');
    }
    
    const response = await api.put(`/entities/${entityId}`, entityData);
    return response.data;
  },

  // Delete entity
  deleteEntity: async (entityId: string): Promise<void> => {
    if (config.shouldUseDemoMode()) {
      throw new Error('Entity deletion not available in demo mode');
    }
    
    await api.delete(`/entities/${entityId}`);
  },

  // Upload files to entity
  uploadFiles: async (entityId: string, files: File[]): Promise<any> => {
    if (config.shouldUseDemoMode()) {
      throw new Error('File upload not available in demo mode');
    }
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post(`/entities/${entityId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Add URLs to entity
  addUrls: async (entityId: string, urls: string[]): Promise<any> => {
    if (config.shouldUseDemoMode()) {
      throw new Error('URL addition not available in demo mode');
    }
    
    const response = await api.post(`/entities/${entityId}/urls`, { urls });
    return response.data;
  },

  // Process entity
  processEntity: async (entityId: string): Promise<any> => {
    if (config.shouldUseDemoMode()) {
      throw new Error('Entity processing not available in demo mode');
    }
    
    const response = await api.post(`/entities/${entityId}/process`);
    return response.data;
  },
};
